/**
 * Camada 1c · escolhe o transporte da conversa pelo PLANO declarado no manifesto.
 *
 * O DEFEITO QUE ISTO FECHA, medido em 05/09/2026: existiam DUAS implementações do sino. A do
 * `packages/chat` fala com `services/messaging` e manda `X-Tenant-Id`; a do
 * `apps/admin/src/dados/usar-sino-admin.ts`, com 80 linhas, fala com o `admin-api` e NÃO manda
 * tenant, porque o operador do plano de controle não tem nenhum.
 *
 * A DIFERENÇA É REAL E NÃO SOME. O que ela não pode ser é improvisada dentro de cada app: com
 * a escolha declarada no manifesto e resolvida aqui, o código continua sendo um só, e o app
 * novo não tem como escolher errado, porque não escolhe nada, declara.
 *
 * POR QUE UM `switch` E NÃO UM `if`: a lista de planos é FECHADA e POSITIVA. Com `if
 * (plano === 'control-plane')`, um plano futuro cairia no ramo do tenant sem ninguém decidir,
 * e o sintoma seria 401 num app novo. Com o `switch` exaustivo, o TypeScript recusa compilar
 * quando um plano novo aparecer sem tratamento.
 */
import { useMemo } from 'react';
import { usarSino, type EstadoSino } from '@complexity/chat';
import type { MensagensProduto } from '@complexity/product-kit';
import { BASES } from './bases.js';
import type { Credenciais } from './credenciais.js';
import { usarSinoDoControlPlane } from './transporte-control-plane.js';

/**
 * Roda o sino do plano certo.
 *
 * OS DOIS GANCHOS SÃO CHAMADOS SEMPRE, e isto não é desperdício: é a regra dos hooks do React.
 * Chamar um ou outro conforme o plano faria a quantidade de hooks mudar entre renders, e o
 * React derruba a árvore inteira com "Rendered more hooks than during the previous render". O
 * defeito exato aconteceu no `admin` em 05/09/2026 e deixou a tela BRANCA depois do login.
 *
 * O QUE EVITA A CHAMADA DE REDE não é deixar de chamar o gancho: é cada um deles sair do
 * efeito sem pedir nada quando não é o escolhido. Os dois já fazem isso com base vazia.
 */
export function usarMensagens(mensagens: MensagensProduto, credenciais: Credenciais): EstadoSino {
  /**
   * 🔴 O `useMemo` AQUI NAO E OTIMIZACAO, E A CORRECAO DE UM LACO INFINITO.
   *
   * O DEFEITO, medido na tela dele em 06/09/2026: este objeto era criado direto no corpo da
   * funcao, e por isso nascia com IDENTIDADE NOVA a cada render. `usarSino` o tem como
   * dependencia de efeito: identidade nova significa dependencia mudada, entao ele buscava de
   * novo, o estado mudava, o componente renderizava, um objeto novo nascia, e recomecava.
   *
   * O NUMERO: `/conversas/nao-lidas` era chamado a cada 27 MILISSEGUNDOS, quando o intervalo
   * declarado e de 2 segundos. Sao mais de setenta chamadas por segundo, por aba aberta.
   *
   * O QUE ISSO QUEBRA, e nao e o sino: o navegador limita as conexoes simultaneas por
   * servidor, e o laco as consome todas. As OUTRAS chamadas da tela passam a falhar por falta
   * de conexao, e a Central de Ajuda mostrava "a base de conhecimento nao respondeu" com o
   * servico de pe e respondendo 200 a todo mundo.
   *
   * O SINTOMA NAO FALAVA DO SINO. Foi preciso capturar cada requisicao do navegador para ver
   * que o culpado era o vizinho, e nao a base de conhecimento.
   */
  const cred = useMemo(
    () => ({
      obter: () => {
        const { token, tenantId } = credenciais.obter();
        return { token: token ?? undefined, tenantId: tenantId ?? undefined };
      },
    }),
    // `credenciais` ja e estavel: ele vem de `usarCredenciais`, que e um `useMemo` sobre os
    // VALORES. Entao esta dependencia so muda quando token, cliente ou e-mail mudam de fato.
    [credenciais],
  );

  /**
   * SEM SESSAO, O SINO NAO PERGUNTA NADA.
   *
   * O DEFEITO, medido em 06/09/2026: na tela de entrada, antes de qualquer login, o sino ja
   * disparava contra `/conversas/nao-lidas` e tomava 401. Duas chamadas por carga de pagina,
   * mais as do relogio enquanto a pessoa digita a senha.
   *
   * POR QUE ISSO IMPORTA, e nao e so ruido: 401 e a resposta certa do servico, entao um
   * monitor que vigia taxa de erro passa a apitar por causa de gente parada na tela de login.
   * E quem for depurar um 401 de verdade vai encontra-lo no meio de dezenas de 401 esperados.
   *
   * PERGUNTAR SEM CREDENCIAL NAO E DEGRADACAO, E DESPERDICIO: a resposta e conhecida antes de
   * a chamada sair.
   */
  const { token, tenantId } = credenciais.obter();

  /**
   * CADA PLANO TEM UMA CONDICAO DIFERENTE PARA PODER PERGUNTAR, e trata-los igual foi o erro
   * da primeira correcao deste bloco.
   *
   * O PLANO `tenant` PRECISA DOS DOIS: token E cliente. Medido em 06/09/2026: depois do login
   * o token ja existe, mas o `tenantId` so chega quando o app busca os vinculos, e nesse
   * intervalo o sino disparava com token e SEM cliente. O `services/messaging` recusa com 401
   * e a mensagem "Cabecalho X-Tenant-Id ausente", que esta certa.
   *
   * O PLANO `control-plane` PRECISA SO DO TOKEN, e exigir cliente ali seria o oposto do
   * correto: o operador atravessa todos os clientes e nao tem nenhum. Foi essa diferenca que a
   * primeira versao apagou, ao usar uma condicao unica para os dois.
   *
   * PERGUNTAR SEM PODER RESPONDER NAO E DEGRADACAO, E DESPERDICIO: a resposta e conhecida
   * antes de a chamada sair, e o 401 esperado polui o log de quem procura um 401 de verdade.
   */
  const podeTenant = token !== null && tenantId !== null;
  const podeControlPlane = token !== null;

  // O sino do plano do CLIENTE. Base `undefined` quando o plano não é este ou quando ainda
  // falta credencial, e aí ele nasce e permanece indisponível sem disparar chamada nenhuma.
  const doTenant = usarSino(
    podeTenant && mensagens.plano === 'tenant' ? BASES.mensagens : undefined,
    mensagens.lado,
    cred,
  );

  // O sino do PLANO DE CONTROLE, pela mesma lógica e com a condição dele.
  const doControlPlane = usarSinoDoControlPlane(
    podeControlPlane && mensagens.plano === 'control-plane' ? BASES.controlPlane : undefined,
    credenciais,
  );

  switch (mensagens.plano) {
    case 'tenant':
      return doTenant;
    case 'control-plane':
      return doControlPlane;
    default: {
      // A EXAUSTIVIDADE É A TRANCA: se um plano novo entrar no tipo sem entrar aqui, esta
      // atribuição deixa de compilar. É o typecheck recusando, e não uma revisão lembrando.
      const naoTratado: never = mensagens.plano;
      throw new Error(`Plano de mensagens sem tratamento: ${String(naoTratado)}`);
    }
  }
}
